import React, { lazy, Suspense, useContext } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import axios from "axios";
import "./App.css";
import { AuthContext } from "./context/AuthContext";
import Navbar from "./components/navbar/Navbar";
import AdminNavbar from "./components/adminNavbar/AdminNavbar";
import UpdateShelf from "./pages/shelf/UpdateShelf/UpdateShelf";
import NewProduct from "./pages/product/newProduct/NewProduct";
import UpdateProduct from "./pages/product/updateProduct/UpdateProduct";
import NewSupplier from "./pages/supplier/newSupplier/NewSupplier";
import UpdateSupplier from "./pages/supplier/updateSupplier/UpdateSupplier";
import SupplierProfile from "./pages/supplier/supplierProfile/SupplierProfile";
import CustomerProfile from "./pages/customer/customerProfile/CustomerProfile";
import NewPurchaseInvoice from "./pages/purchaseInvoice/NewPurchaseInvoice";
import PurchaseInvoices from "./pages/purchaseInvoice/purchaseInvoices/PurchaseInvoices";
import PurchaseInvoiceDetails from "./pages/purchaseInvoice/purchaseInvoiceDetails/PurchaseInvoiceDetails";
import SalesInvoices from "./pages/invoice/salesInvoices/SalesInvoices";
import ReturnedInvoices from "./pages/invoice/returnedInvoices/ReturnedInvoices";
import SalesInvoiceDetails from "./pages/invoice/saleInvoiceDetails/SaleInvoiceDetails";
import BarcodeEntry from "./pages/barcode/BarcodeEntry";
import NewReceiptVoucher from "./pages/receiptVoucher/NewReceiptVoucher";
import ReceiptVouchers from "./pages/receiptVoucher/ReceiptVouchers";
import ReceiptVoucherDetails from "./pages/receiptVoucher/ReceiptVoucherDetails";
import NewPaymentVoucher from "./pages/paymentVoucher/NewPaymentVoucher";
import PaymentVouchers from "./pages/paymentVoucher/PaymentVouchers";
import PaymentVoucherDetails from "./pages/paymentVoucher/PaymentVoucherDetails";
import ExpenseInvoices from "./pages/expenseInvoice/ExpenseInvoices";
import ExpenseInvoiceForm from "./pages/expenseInvoice/ExpenseInvoiceForm";
import ExpenseInvoiceDetails from "./pages/expenseInvoice/ExpenseInvoiceDetails";

const Login = lazy(() => import("./pages/auth/login/Login"));
const ChooseBranch = lazy(() =>
  import("./pages/auth/chooseBranch/ChooseBranch")
);
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminBranches = lazy(() => import("./pages/admin/Branches"));
const QuantityRequests = lazy(() =>
  import("./pages/product/quantityRequests/QuantityRequests")
);
const ForgetPassword = lazy(() =>
  import("./pages/auth/forgetPassword/ForgetPassword")
);
const ResetPassword = lazy(() =>
  import("./pages/auth/resetPassword/ResetPassword")
);
const Shelves = lazy(() => import("./pages/shelf/shelves/Shelves"));
const ShelfProfile = lazy(() =>
  import("./pages/shelf/ShelfProfile/ShelfProfile")
);
const NewShelf = lazy(() => import("./pages/shelf/NewShelf/NewShelf"));
const Products = lazy(() => import("./pages/product/products/Products"));
const MainPage = lazy(() => import("./pages/mainPage/MainPage"));
const Cart = lazy(() => import("./pages/cart/Cart"));
const NewCustomer = lazy(() =>
  import("./pages/customer/newCustomer/NewCustomer")
);
const UpdateCustomer = lazy(() =>
  import("./pages/customer/updateCustomer/UpdateCustomer")
);
const Customers = lazy(() => import("./pages/customer/customers/Customers"));
const Suppliers = lazy(() => import("./pages/supplier/suppliers/Suppliers"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const Debts = lazy(() => import("./pages/debts/Debts"));
const CustomerStatement = lazy(() =>
  import("./pages/statements/CustomerStatement")
);
const SupplierStatement = lazy(() =>
  import("./pages/statements/SupplierStatement")
);

axios.defaults.withCredentials = true;

// ✅ حماية الصفحات
const PrivateRoute = ({ children }) => {
  const { user, loading, requiresBranchSelection } = useContext(AuthContext);

  if (loading) return <div>جارٍ التحقق من تسجيل الدخول...</div>;
  if (!user) return <Navigate to="/login" />;
  // 🏢 المدير العام الذي لم يختر فرعاً بعد → صفحة اختيار الفرع
  if (requiresBranchSelection) return <Navigate to="/choose-branch" />;
  return children;
};

// ✅ حماية صفحات المدير العام فقط
const SuperAdminRoute = ({ children }) => {
  const { user, loading, requiresBranchSelection } = useContext(AuthContext);

  if (loading) return <div>جارٍ التحقق...</div>;
  if (!user) return <Navigate to="/login" />;
  if (requiresBranchSelection) return <Navigate to="/choose-branch" />;
  if (user.role !== "SUPER_ADMIN") return <Navigate to="/" />;
  return children;
};

// ✅ مكون Wrapper حتى نتحكم في إظهار الـ Navbar
const AppContent = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();

  // الصفحات التي لا نريد عرض الـ Navbar فيها
  const hideNavbarRoutes = [
    "/login",
    "/forget-password",
    "/reset-password",
    "/choose-branch",
  ];
  const shouldHideNavbar = hideNavbarRoutes.includes(location.pathname);

  return (
    <>
      {/* ✅ Navbar يظهر فقط إذا المستخدم مسجل دخول والصفحة ليست من صفحات تسجيل الدخول */}
      {user && !shouldHideNavbar && <Navbar />}
      {/* 🛡️ شريط المدير العام أسفل الشريط الرئيسي (يظهر داخلياً فقط للـ SUPER_ADMIN) */}
      {user && !shouldHideNavbar && <AdminNavbar />}

      <Suspense fallback={<div>جارٍ التحميل...</div>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/choose-branch"
            element={user ? <ChooseBranch /> : <Navigate to="/login" />}
          />
          <Route path="/forget-password" element={<ForgetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* صفحات محمية */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <MainPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/cart"
            element={
              <PrivateRoute>
                <Cart />
              </PrivateRoute>
            }
          />
          <Route
            path="/new-product"
            element={
              <PrivateRoute>
                <NewProduct />
              </PrivateRoute>
            }
          />
          <Route
            path="/products"
            element={
              <PrivateRoute>
                <Products />
              </PrivateRoute>
            }
          />
          <Route
            path="/shelves"
            element={
              <PrivateRoute>
                <Shelves />
              </PrivateRoute>
            }
          />
          <Route
            path="/new-shelf"
            element={
              <PrivateRoute>
                <NewShelf />
              </PrivateRoute>
            }
          />
          <Route
            path="/update-shelf/:id"
            element={
              <PrivateRoute>
                <UpdateShelf />
              </PrivateRoute>
            }
          />
          <Route
            path="/shelf/:id"
            element={
              <PrivateRoute>
                <ShelfProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/update-product/:id"
            element={
              <PrivateRoute>
                <UpdateProduct />
              </PrivateRoute>
            }
          />
          <Route
            path="/new-customer"
            element={
              <PrivateRoute>
                <NewCustomer />
              </PrivateRoute>
            }
          />
          <Route
            path="/new-supplier"
            element={
              <PrivateRoute>
                <NewSupplier />
              </PrivateRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <PrivateRoute>
                <Customers />
              </PrivateRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <PrivateRoute>
                <Suppliers />
              </PrivateRoute>
            }
          />
          <Route
            path="/update-customer/:id"
            element={
              <PrivateRoute>
                <UpdateCustomer />
              </PrivateRoute>
            }
          />
          <Route
            path="/update-supplier/:id"
            element={
              <PrivateRoute>
                <UpdateSupplier />
              </PrivateRoute>
            }
          />
          <Route
            path="/supplier/:id"
            element={
              <PrivateRoute>
                <SupplierProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/customer/:id"
            element={
              <PrivateRoute>
                <CustomerProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/new-purchase-invoice"
            element={
              <PrivateRoute>
                <NewPurchaseInvoice />
              </PrivateRoute>
            }
          />
          <Route
            path="/purchase-invoices"
            element={
              <PrivateRoute>
                <PurchaseInvoices />
              </PrivateRoute>
            }
          />
          <Route
            path="/purchase-invoice/:id"
            element={
              <PrivateRoute>
                <PurchaseInvoiceDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/sales-invoices"
            element={
              <PrivateRoute>
                <SalesInvoices />
              </PrivateRoute>
            }
          />
          <Route
            path="/returned-invoices"
            element={
              <PrivateRoute>
                <ReturnedInvoices />
              </PrivateRoute>
            }
          />
          <Route
            path="/sales-invoice/:id"
            element={
              <PrivateRoute>
                <SalesInvoiceDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/barcode-entry"
            element={
              <PrivateRoute>
                <BarcodeEntry />
              </PrivateRoute>
            }
          />
          <Route
            path="/new-receipt-voucher"
            element={
              <PrivateRoute>
                <NewReceiptVoucher />
              </PrivateRoute>
            }
          />
          <Route
            path="/receipt-vouchers"
            element={
              <PrivateRoute>
                <ReceiptVouchers />
              </PrivateRoute>
            }
          />
          <Route
            path="/receipt-voucher/:id"
            element={
              <PrivateRoute>
                <ReceiptVoucherDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/new-payment-voucher"
            element={
              <PrivateRoute>
                <NewPaymentVoucher />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment-vouchers"
            element={
              <PrivateRoute>
                <PaymentVouchers />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment-voucher/:id"
            element={
              <PrivateRoute>
                <PaymentVoucherDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/expense-invoices"
            element={
              <PrivateRoute>
                <ExpenseInvoices />
              </PrivateRoute>
            }
          />
          <Route
            path="/expense-invoice/new"
            element={
              <PrivateRoute>
                <ExpenseInvoiceForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/expense-invoice/edit/:id"
            element={
              <PrivateRoute>
                <ExpenseInvoiceForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/expense-invoice/:id"
            element={
              <PrivateRoute>
                <ExpenseInvoiceDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/debts"
            element={
              <PrivateRoute>
                <Debts />
              </PrivateRoute>
            }
          />
          <Route
            path="/customer-statement"
            element={
              <PrivateRoute>
                <CustomerStatement />
              </PrivateRoute>
            }
          />
          <Route
            path="/supplier-statement"
            element={
              <PrivateRoute>
                <SupplierStatement />
              </PrivateRoute>
            }
          />

          {/* 🛡️ صفحات المدير العام */}
          <Route
            path="/admin/users"
            element={
              <SuperAdminRoute>
                <AdminUsers />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/branches"
            element={
              <SuperAdminRoute>
                <AdminBranches />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/quantity-requests"
            element={
              <SuperAdminRoute>
                <QuantityRequests />
              </SuperAdminRoute>
            }
          />
        </Routes>
      </Suspense>
    </>
  );
};

function App() {
  return (
    <>
      <AppContent />
    </>
  );
}

export default App;
